import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * Emergent Object Storage upload component.
 * Props:
 *   value          — current image URL (or empty)
 *   onChange(url)  — called with the uploaded absolute URL (or "" on clear)
 *   label          — text above the dropzone
 *   testid         — optional data-testid prefix
 *   aspect         — tailwind aspect class, default "aspect-square"
 */
export default function FileUpload({ value, onChange, label = "Image", testid = "file-upload", aspect = "aspect-square" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, WEBP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large. Max 8MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Uploaded");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    upload(file);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    upload(file);
  };

  return (
    <div data-testid={testid}>
      {label && <div className="overline mb-2">{label}</div>}

      {value ? (
        <div className="relative group border border-border">
          <div className={`${aspect} overflow-hidden bg-muted`}>
            <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
          </div>
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="btn-primary text-xs px-4 py-2"
              disabled={busy}
              data-testid={`${testid}-replace`}
            >
              {busy ? "Uploading…" : "Replace"}
            </button>
            <button
              type="button"
              onClick={() => onChange?.("")}
              className="btn-outline text-xs px-4 py-2 bg-background"
              data-testid={`${testid}-clear`}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`w-full ${aspect} border border-dashed flex flex-col items-center justify-center gap-2 text-foreground/60 hover:text-foreground hover:border-primary transition-colors ${dragOver ? "border-primary bg-muted" : "border-border"}`}
          disabled={busy}
          data-testid={`${testid}-dropzone`}
        >
          {busy ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              <span className="text-xs uppercase tracking-[0.18em]">Uploading…</span>
            </>
          ) : (
            <>
              <Upload size={20} />
              <span className="text-xs uppercase tracking-[0.18em]">Click or drop image</span>
              <span className="text-[10px] text-foreground/50">JPG · PNG · WEBP · Max 8MB</span>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPick}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
